import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {DateField} from '@mui/x-date-pickers';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {Moment} from 'moment';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DateRangeCalendar} from '../date-range-calendar/date-range-calendar';
import {translations} from '../../i18n/translations';
import {
  calcAmount,
  calcAmountType,
  calcDateByAmount,
  DateRangeType,
  calcDatesByRange,
  calcRangeTypeByDates,
  calcNewDates,
  DateRangeMode,
  DateRangeOnChange,
} from './date-range-utils';
import {maxDateTimeAllow} from '../../screens/private/office/professional-caregiver/planner/calendar/shared/date-max-time';
import {fadeInRight} from '../../utils/css-animations';

export type DateRangeDropdownProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  startDate: Moment;
  endDate: Moment;
  mode: DateRangeMode;
  disabledFuture?: boolean;
  onClose: () => void;
  onConfirm: DateRangeOnChange;
};

export function DateRangeDropdown(props: DateRangeDropdownProps) {
  const [startDate, setStartDate] = useState<Moment>(props.startDate);
  const [endDate, setEndDate] = useState<Moment>(props.endDate);
  const [range, setRange] = useState<DateRangeType>(calcRangeTypeByDates(startDate, endDate));
  const {t} = useTranslation();

  const maxDate: Moment | undefined = useMemo(() => {
    return maxDateTimeAllow();
  }, []);

  const invalidDate = useMemo(() => {
    const validDates = startDate.isValid() && endDate.isValid();

    return {
      startDate: !validDates || startDate.isAfter(endDate),
      endDate:
        !validDates ||
        endDate.isBefore(startDate) ||
        (startDate.isAfter(maxDate) && endDate.isAfter(maxDate)),
      futureEndDate: !validDates || endDate.isAfter(maxDate),
    };
  }, [maxDate, startDate, endDate]);

  const handleChangeRange = (range: DateRangeType) => {
    const newDates = calcDatesByRange(startDate, endDate, range);
    setRange(range);
    setStartDate(newDates.startDate);
    setEndDate(newDates.endDate);
  };

  const handleStartDateFieldChange = (date: Moment | null) => {
    if (!date) return;

    // A single day: the end is the same day
    if (props.mode === 'day') {
      setStartDate(date.clone().startOf('day'));
      setEndDate(date.clone().endOf('day'));
      return;
    }

    setRange('custom');
    setStartDate(date);
  };

  const handleEndDateFieldChange = (date: Moment | null) => {
    if (!date) return;

    setRange('custom');
    setEndDate(date);
  };

  const handleBackClick = () => {
    // Calculate new dates range
    const newDates = calcNewDates(startDate, endDate, 'subtract');

    setStartDate(newDates.startDate);
    setEndDate(newDates.endDate);
  };

  const handleNextClick = () => {
    // Calculate new dates range
    const newDates = calcNewDates(startDate, endDate, 'add');

    setStartDate(newDates.startDate);
    setEndDate(newDates.endDate);
  };

  const handleCancel = () => {
    props.onClose();
  };

  const handleConfirm = () => {
    // Safety check
    if (!startDate.isValid() || !endDate.isValid()) return;

    // Fire event
    props.onConfirm(startDate, endDate, range);
  };

  const arrowBtnSx: SxProps = {
    padding: 0,
    minWidth: 0,
    width: '25px',
    height: '100%',
    '& .MuiSvgIcon-root': {
      fontSize: '32px',
    },
    color: 'gray.main',
    '&:hover': {
      backgroundColor: 'primary.bg',
    },
  };

  const anyDateEror = invalidDate.startDate || invalidDate.endDate;

  return (
    <Menu
      anchorEl={props.anchorEl}
      open
      onClose={props.onClose}
      sx={{
        '& .MuiList-root': {
          overflow: 'auto',
          maxWidth: '490px',
        },
      }}
    >
      <Box sx={{margin: '5px 10px'}}>
        {/* --- TITLE --- */}
        <Typography variant="h6" sx={{textAlign: 'center'}}>
          {t(translations.date_range_input.title)}
        </Typography>

        <Stack flexDirection="row">
          {/* --- SIDE RANGES TYPES --- */}
          {props.mode !== 'day' && (
            <List sx={{minWidth: '140px'}}>
              <ListItem disablePadding>
                <ListItemButton selected={range === '1d'} onClick={() => handleChangeRange('1d')}>
                  <ListItemText primary={t(translations.date_range_input.presets.day)} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton selected={range === '1w'} onClick={() => handleChangeRange('1w')}>
                  <ListItemText primary={t(translations.date_range_input.presets.week)} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton selected={range === '1m'} onClick={() => handleChangeRange('1m')}>
                  <ListItemText primary={t(translations.date_range_input.presets.month)} />
                </ListItemButton>
              </ListItem>
              {props.mode === 'default' && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === '7d'}
                      onClick={() => handleChangeRange('7d')}
                    >
                      <ListItemText primary={t(translations.date_range_input.presets['7_days'])} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === '15d'}
                      onClick={() => handleChangeRange('15d')}
                    >
                      <ListItemText primary={t(translations.date_range_input.presets['15_days'])} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === '30d'}
                      onClick={() => handleChangeRange('30d')}
                    >
                      <ListItemText primary={t(translations.date_range_input.presets['30_days'])} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === '180d'}
                      onClick={() => handleChangeRange('180d')}
                    >
                      <ListItemText
                        primary={t(translations.date_range_input.presets['180_days'])}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === '365d'}
                      onClick={() => handleChangeRange('365d')}
                    >
                      <ListItemText
                        primary={t(translations.date_range_input.presets['365_days'])}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={range === 'custom'}
                      onClick={() => handleChangeRange('custom')}
                    >
                      <ListItemText primary={t(translations.date_range_input.presets.custom)} />
                    </ListItemButton>
                  </ListItem>
                </>
              )}
            </List>
          )}

          <Box sx={{py: '8px'}}>
            {/* --- <DATE> até <DATE> --- */}
            <Stack
              sx={{flexDirection: 'row', placeItems: 'center', placeContent: 'center', gap: '8px'}}
            >
              <Button onClick={handleBackClick} disabled={invalidDate.startDate} sx={arrowBtnSx}>
                <KeyboardArrowLeft />
              </Button>
              <DateField
                size="small"
                value={startDate}
                sx={{maxWidth: '116px'}}
                maxDate={maxDate}
                shouldDisableDate={() => invalidDate.startDate}
                disableFuture={props.disabledFuture}
                disabled={props.mode === 'strict'}
                onChange={handleStartDateFieldChange}
              />
              {props.mode !== 'day' && (
                <>
                  <Typography variant="body1">{t(translations.time_lapse.to_text)}</Typography>
                  <DateField
                    size="small"
                    value={endDate}
                    sx={{maxWidth: '116px'}}
                    maxDate={maxDate}
                    shouldDisableDate={() => invalidDate.endDate}
                    disableFuture={props.disabledFuture}
                    disabled={props.mode === 'strict'}
                    onChange={handleEndDateFieldChange}
                  />
                </>
              )}
              <Button
                onClick={handleNextClick}
                disabled={invalidDate.futureEndDate}
                sx={arrowBtnSx}
              >
                <KeyboardArrowRight />
              </Button>
            </Stack>

            {/* --- CALENDAR --- */}
            <DateRangeCalendar
              start={startDate}
              end={endDate}
              maxDate={maxDate}
              disableFuture={props.disabledFuture}
              onChange={(selectedDate) => {
                const amountType = calcAmountType(startDate, endDate);
                const amount = calcAmount(startDate, endDate, amountType);
                const newStartDate = calcDateByAmount(selectedDate, amount, amountType, 'subtract');
                const dates = calcDatesByRange(newStartDate, selectedDate, range);

                setStartDate(dates.startDate);
                setEndDate(dates.endDate);
              }}
            />
          </Box>
        </Stack>

        {/* --- BUTTONS --- */}
        <Stack flexDirection="row" sx={{placeContent: 'end'}}>
          <Button onClick={handleCancel} color="inherit">
            {t(translations.date_range_input.cancel_button)}
          </Button>
          <Button onClick={handleConfirm} color="primary" disabled={anyDateEror}>
            {t(translations.date_range_input.ok_button)}
          </Button>
        </Stack>

        {/* --- ERROR MESSAGE --- */}
        {anyDateEror && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '50px',
              right: '18px',
              width: '100%',
              animation: `${fadeInRight} 0.3s`,
              textAlign: 'right',
            }}
          >
            <Typography variant="subtitle2" color="error.main">
              {t(translations.date_range_input.errors.invalid_date_range)}
            </Typography>
          </Box>
        )}
      </Box>
    </Menu>
  );
}
